import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  httpGetVirtualCardStats,
  httpGetVirtualCardsList,
} from "api/admin/virtual-card";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

interface VirtualCardStats {
  totalCardsIssued: number;
  totalCardUsers: number;
  totalMastercard: number;
  totalVisa: number;
  totalFrozen: number;
  totalTerminated: number;
}

interface Card {
  customerId: string;
  cardId: string;
  balance: number;
  cardBrand: string;
  cardNumber: string;
  createdAt: string;
  status: string;
}

const VirtualCardsDashboard: React.FC = () => {
  const { can } = useAdminAccess();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search] = useState("");
  const [cardId, setCardId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showActivated, setShowActivated] = useState(true);
  const [showTerminated, setShowTerminated] = useState(true);

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ["virtualCardStats"],
    () => httpGetVirtualCardStats(), { enabled: can("virtualCard.stats.view") },
  );

  const { data: cardsData, isLoading: cardsLoading } = useQuery(
    [
      "virtualCardsList",
      currentPage,
      pageSize,
      search,
      cardId,
      fromDate,
      toDate,
      showActivated,
      showTerminated,
    ],
    () =>
      httpGetVirtualCardsList({
        lastName: search || undefined,
        cardId: cardId || undefined,
        FromDate: fromDate || undefined,
        ToDate: toDate || undefined,
        activated: showActivated ? undefined : false,
        terminated: showTerminated ? undefined : false,
        PageNumber: currentPage,
        PageSize: pageSize,
      }),
  );

  const stats: VirtualCardStats = statsData || {
    totalCardsIssued: 0,
    totalCardUsers: 0,
    totalMastercard: 0,
    totalVisa: 0,
    totalFrozen: 0,
    totalTerminated: 0,
  };

  const cards: Card[] = cardsData?.cards || [];
  const pagination = cardsData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div>
          <h1 className="mb-8 text-3xl font-bold text-gray-900">
            Virtual Cards
          </h1>

          <AdminPermission permission={"virtualCard.stats.view"}>
            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Overview</h2>
              {statsLoading && <p className="py-10 text-center text-gray-500">Loading...</p>}
              {!statsLoading && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="mb-1 text-sm text-gray-600">Total Cards Issued</p>
                        <p className="text-3xl font-bold text-gray-900">
                          {stats.totalCardsIssued}
                        </p>
                      </div>
                      <div className="text-3xl text-blue-600">
                        <i className="fa fa-credit-card"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="mb-1 text-sm text-gray-600">Total Card Users</p>
                        <p className="text-3xl font-bold text-gray-900">{stats.totalCardUsers}</p>
                      </div>
                      <div className="text-3xl text-green-600">
                        <i className="fa fa-users"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <p className="mb-3 text-sm text-gray-600">Total Mastercard</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalMastercard}</p>
                  </div>
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <p className="mb-3 text-sm text-gray-600">Total Visa</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalVisa}</p>
                  </div>
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="mb-1 text-sm text-gray-600">Total Frozen</p>
                        <p className="text-3xl font-bold text-gray-900">{stats.totalFrozen}</p>
                      </div>
                      <div className="text-3xl text-cyan-600">
                        <i className="fa fa-snowflake"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="mb-1 text-sm text-gray-600">Total Terminated</p>
                        <p className="text-3xl font-bold text-gray-900">
                          {stats.totalTerminated}
                        </p>
                      </div>
                      <div className="text-3xl text-red-700">
                        <i className="fa fa-ban"></i>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AdminPermission>

          <div>
            <h2 className="mb-6 text-xl font-bold text-gray-900">
              Virtual Cards List
            </h2>

            <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Card ID
                  </label>
                  <input
                    type="text"
                    value={cardId}
                    onChange={(e) => {
                      setCardId(e.target.value);
                      setCurrentPage(1);
                    }}
                    placeholder="Search by card ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showActivated}
                    onChange={(e) => {
                      setShowActivated(e.target.checked);
                      setCurrentPage(1);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showTerminated}
                    onChange={(e) => {
                      setShowTerminated(e.target.checked);
                      setCurrentPage(1);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Terminated
                  </span>
                </label>
              </div>
            </div>

            {cardsLoading && (
              <p className="py-10 text-center text-gray-500">
                Loading cards...
              </p>
            )}
            {!cardsLoading && cards.length === 0 && (
              <p className="py-10 text-center text-gray-500">No cards found</p>
            )}
            {!cardsLoading && cards.length > 0 && (
              <div className="overflow-hidden bg-white rounded-lg shadow">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-base font-semibold text-left text-gray-900">
                          <i className="mr-2 text-gray-500 fa fa-credit-card"></i>
                          Card Number
                        </th>
                        <th className="px-6 py-4 text-base font-semibold text-left text-gray-900">
                          Brand
                        </th>
                        <th className="px-6 py-4 text-base font-semibold text-left text-gray-900">
                          <i className="mr-2 text-gray-500 fa fa-wallet"></i>
                          Balance
                        </th>
                        <th className="px-6 py-4 text-base font-semibold text-left text-gray-900">
                          Status
                        </th>
                        <th className="px-6 py-4 text-base font-semibold text-left text-gray-900">
                          <i className="mr-2 text-gray-500 fa fa-calendar"></i>
                          Created At
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((card) => (
                        <tr
                          key={card.cardId}
                          className="transition-colors border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            navigate(`/virtual-cards/all/card/${card.cardId}`)
                          }
                        >
                          <td className="px-6 py-4 font-mono text-base text-gray-900">
                            {card.cardNumber}
                          </td>
                          <td className="px-6 py-4 text-base text-gray-900">
                            {card.cardBrand}
                          </td>
                          <td className="px-6 py-4 text-base font-semibold text-gray-900">
                            ${card.balance.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 text-base">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-semibold inline-flex items-center gap-1 ${
                                card.status === "Card - Active"
                                  ? "bg-green-100 text-green-800"
                                  : card.status === "Card - Suspended"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {card.status === "Card - Active" && (
                                <i className="text-sm fa fa-check-circle"></i>
                              )}
                              {card.status === "Card - Suspended" && (
                                <i className="text-sm fa fa-exclamation-circle"></i>
                              )}
                              {card.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-base text-gray-600">
                            {new Date(card.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">
                      {(currentPage - 1) * pageSize + 1}–
                      {Math.min(currentPage * pageSize, pagination.total)}
                    </span>
                    {" of "}
                    <span className="font-medium text-gray-900">
                      {pagination.total}
                    </span>
                    {" results"}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={!pagination.hasPrevPage}
                      className="px-4 py-2 font-medium text-gray-700 transition bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <i className="mr-1 fa fa-chevron-left"></i>Previous
                    </button>
                    <div className="flex items-center gap-1 mx-2">
                      {Array.from(
                        { length: pagination.totalPages },
                        (_, i) => i + 1,
                      ).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 rounded font-medium transition ${
                            page === currentPage
                              ? "bg-blue-600 text-white shadow"
                              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-4 py-2 font-medium text-gray-700 transition bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Next<i className="ml-1 fa fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Main>
    </Container>
  );
};

export default VirtualCardsDashboard;
